import { useState, useEffect, useCallback, useMemo, useContext } from 'react'
import { Slider, Typography, TextField, Box } from '@material-ui/core'
import axiosAuth from '../../../utils/axiosWithAuth'
import OliveTable from "./OliveTable"
import { useUser } from '../../../contexts/UserContext'
import './Olive.scss'
import {debounce} from 'lodash';
import ValidationClientSelector from "./ValidationClientSelector";

/**
 * A panel that contains the confidence slider and date range pickers,
 * centered and grouped together.
 */
function FilterPanel({
  dateRange,
  confidenceRange,
  onConfidenceChange,
  onStartDateChange,
  onEndDateChange,
  onApplyFilters,
  formatDate,
  client,
  setClient,
  user
}) {
  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent='center'
      width="100%"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        mb={4}
        width="20%"
      >
        <Typography id="confidence-range-slider" gutterBottom>
          Confidence Range: {confidenceRange[0]}% – {confidenceRange[1]}%
        </Typography>
        <Box width={300}>
          <Slider
            value={confidenceRange}
            onChange={onConfidenceChange}
            onChangeCommitted={onApplyFilters}
            valueLabelDisplay="auto"
            aria-labelledby="confidence-range-slider"
            min={0}
            max={100}
          />
        </Box>
      </Box>
      {user.admin && 
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          mb={4}
          width="20%"
        >
          <Typography gutterBottom style={{ marginTop: '2rem', marginBottom: '1rem' }}>
            Client
          </Typography>
          <Box display="flex" gap="1rem">
            <ValidationClientSelector
              client={client}
              setClient={setClient}
            />
          </Box>
        </Box>
      }
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        mb={4}
        width="20%"
      >
        <Typography gutterBottom style={{ marginTop: '2rem', marginBottom: '1rem' }}>
          Bill Creation Date Range
        </Typography>
        <Box display="flex" gap="1rem">
          <TextField
            label="Start Date"
            type="date"
            value={formatDate(dateRange[0])}
            onChange={e => {
              const d = new Date(e.target.value)
              onStartDateChange(d)
              onApplyFilters()
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={formatDate(dateRange[1])}
            onChange={e => {
              const d = new Date(e.target.value)
              onEndDateChange(d)
              onApplyFilters()
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default function Olive() {
  const [validations, setValidations] = useState([])
  const [filters, setFilter] = useState(null)
  const [confidenceRange, setConfidenceRange] = useState([0, 70])
  const [client, setClient] = useState("all")
  const [page] = useState(1)
  const [sort] = useState("id")
  const [order] = useState("asc")

  // DateRange: yesterday to tomorrow
  const getDefaultDateRange = () => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    return [yesterday, tomorrow]
  }
  const [dateRange, setDateRange] = useState(getDefaultDateRange())

  const { user } = useUser()

  const fetchValidations = useCallback(() => {
    axiosAuth()
      .post(
        `/${user.admin ? 'admin/textract_bills' : 'documents'}/ai_validations`,
        { filters, order, sort, page, confidenceRange, dateRange, client }
      )
      .then(r => setValidations(r.data.validations))
      .catch(err => console.error(err))
  }, [filters, order, sort, page, confidenceRange, dateRange, user, client])

  // Immediately fetch on mount/update
  useEffect(() => {
    fetchValidations()
  }, [fetchValidations])

  // Debounce the fetchValidations for filter panel
  const debouncedFetchValidations = useMemo(
    () => debounce(fetchValidations, 500),
    [fetchValidations]
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetchValidations.cancel()
    }
  }, [debouncedFetchValidations])

  const handleConfidenceChange = (e, newVal) => setConfidenceRange(newVal)
  const handleStartDateChange = newDate => setDateRange([newDate, dateRange[1]])
  const handleEndDateChange = newDate => setDateRange([dateRange[0], newDate])

  const formatDate = date => date.toISOString().split('T')[0]

  return (
    <>
      <FilterPanel
        dateRange={dateRange}
        confidenceRange={confidenceRange}
        onConfidenceChange={handleConfidenceChange}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        client={client}
        setClient={setClient}
        onApplyFilters={debouncedFetchValidations}
        formatDate={formatDate}
        user={user}
      />
      <OliveTable
        validations={validations}
        fetchValidations={fetchValidations}
      />
    </>
  )
}